import React, { useEffect } from 'react'

import { Paper } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useConfirm } from 'material-ui-confirm'
import { DeleteOutlineOutlined as DeleteIcon, EditOutlined as EditIcon } from '@material-ui/icons'
import { toast } from 'react-toastify'
import Tooltip from '@material-ui/core/Tooltip'

import ActionBar from 'components/ActionBar'
import ViewHeader from 'components/ViewHeader'
import Loading from 'components/Loading'
import getTableOptions from 'utils/getTableOptions'
import ResourceNotFound from 'components/ResourceNotFound'
import BasicTable from 'components/BasicTable'
import { renderCol } from 'utils/renderColumns'
import moderatorSlice, {
  checkModeratorExistsInEventsOrSessions,
  deleteModeratorById,
  fetchModerators
} from 'store/moderator'
import FileUpload from 'components/FileUpload'
import { parseCsvData } from 'utils'
import { bulkInsertModerators } from 'store/moderator'
import Typography from '@material-ui/core/Typography'
import { Box } from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'

const ModeratorListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const confirm = useConfirm()
  const history = useHistory()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const { moderators, currentPage, pageSize, inProgress } = useSelector((state) => state.moderator)

  useEffect(() => {
    dispatch(fetchModerators())
  }, [dispatch])

  const prepareModerators = async (moderatorsList) => {
    try {
      const parsedModerators = parseCsvData(moderatorsList).filter((mod) => mod.fullName)

      if (!parsedModerators.length) {
        toast.error('No moderator exists to upload.')
      } else {
        const existingModerators = moderators.map((mod) => {
          return mod.fullName
        })
        parsedModerators.map((mod) => {
          mod.fullName.trim()
          mod.conferenceId = user?.conferenceId
        })

        const updatedModerators = parsedModerators.filter(
          (mod) => !existingModerators.includes(mod.fullName)
        )
        const uniqueCount = updatedModerators.length
        const existingCount = parsedModerators.length - uniqueCount

        await dispatch(bulkInsertModerators(updatedModerators))

        if (uniqueCount && !existingCount) {
          toast.success(`${uniqueCount} Moderators imported successfully.`)
        } else if (!uniqueCount && existingCount) {
          toast.error(
            `${existingCount} ${
              existingCount === 1
                ? 'Moderator has been excluded due to duplication.'
                : 'Moderators have been excluded due to duplication.'
            }`
          )
        } else {
          toast.success(
            `${uniqueCount} ${
              uniqueCount === 1
                ? 'Moderator imported successfully.'
                : 'Moderators imported successfully.'
            }`
          )
          toast.error(
            `${existingCount} ${
              existingCount === 1
                ? 'Moderator has been excluded due to duplication.'
                : 'Moderators have been excluded due to duplication.'
            }`
          )
        }
      }
    } catch (err) {
      toast.error('Something went wrong while creating moderators.')
    }
  }

  const tableHead = [
    'Full Name',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const formatTableData = (moderators) =>
    moderators.map((moderator) => [
      moderator.fullName,
      <>
        <Tooltip title="Edit">
          <EditIcon
            color="action"
            style={{ cursor: 'pointer' }}
            onClick={() => history.push(`${location.pathname}/edit/${moderator.id}`)}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <DeleteIcon
            color="action"
            style={{ marginLeft: 5, cursor: 'pointer' }}
            onClick={async () => {
              try {
                await confirm({
                  title: 'Delete moderator',
                  description: 'Are you sure you want to delete this moderator?'
                })
                const doesModeratorExist = await dispatch(
                  checkModeratorExistsInEventsOrSessions(moderator.id)
                )
                if (doesModeratorExist) {
                  toast.error(
                    `This moderator cannot be deleted because it is referenced in other event(s) or session(s). To delete it, please remove it from all event(s) and session(s) first.`
                  )
                } else {
                  dispatch(deleteModeratorById(moderator.id))
                }
              } catch {
                // do nothing
              }
            }}
          />
        </Tooltip>
      </>
    ])

  const options = getTableOptions()

  return (
    <>
      <ViewHeader title="Moderators">
        {inProgress && <Loading />}
        <ActionBar
          actionText="New Moderator"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Moderator"
        />
      </ViewHeader>

      <FileUpload headerText="Import moderators from file" onUpload={prepareModerators} />
      <Box className={classes.infoBox}>
        <Typography className={classes.infoText}>
          Please ensure that the CSV file you upload includes a column titled{' '}
          <span className={classes.bold}>&apos;Full Name&apos;</span> for the moderator&apos;s data.
        </Typography>
      </Box>
      {moderators && moderators.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(moderators)}
            columns={tableHead}
            options={{
              ...options,
              onChangePage: (page) => dispatch(moderatorSlice.actions.setCurrentPage(page)),
              onChangeRowsPerPage: (size) => dispatch(moderatorSlice.actions.setPageSize(size)),
              page: currentPage,
              rowsPerPage: pageSize
            }}
          />
        </Paper>
      ) : (
        !inProgress && <ResourceNotFound message="No moderator available" />
      )}
    </>
  )
}

export default ModeratorListing

const useStyles = makeStyles({
  infoBox: {
    marginTop: '16px',
    marginLeft: '10px',
    backgroundColor: '#caedf5',
    padding: '16px',
    width: '70%',
    borderRadius: '10px'
  },
  infoText: {
    fontSize: '14px'
  },
  bold: {
    fontWeight: '600'
  }
})
